import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';

const AuthModal = ({ isOpen, onClose, mode, onSuccess }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    account_type: 'diy',
    broker_tier: 'pro',
    broker_company: ''
  });
  const [loading, setLoading] = useState(false);
  const [showAccountTypeModal, setShowAccountTypeModal] = useState(false);
  const [showIdentityIQImport, setShowIdentityIQImport] = useState(false);
  const [jsonInput, setJsonInput] = useState('');
  const [importing, setImporting] = useState(false);
  
  // Show account type selection when modal opens in register mode
  React.useEffect(() => {
    if (isOpen && mode === 'register') {
      setShowAccountTypeModal(true);
    }
  }, [isOpen, mode]);

  const handleAccountTypeSelect = (type) => {
    if (type === 'identityiq_import') {
      setShowAccountTypeModal(false);
      setShowIdentityIQImport(true);
      setFormData({ ...formData, account_type: 'diy' });
      return;
    }
    
    if (type === 'broker_pro') {
      setFormData({ ...formData, account_type: 'broker', broker_tier: 'pro' });
    } else if (type === 'broker_enterprise') {
      setFormData({ ...formData, account_type: 'broker', broker_tier: 'enterprise' });
    } else {
      setFormData({ ...formData, account_type: type });
    }
    setShowAccountTypeModal(false);
  };

  const handleIdentityIQSignup = async () => {
    // Just register the user, don't require JSON yet
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, formData);
      
      toast.success('✅ Account created! Now import your Identity IQ report to get started.');
      onSuccess(response.data.token, response.data.user);
      onClose();
      
      // User will be redirected to dashboard where they can import JSON
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const endpoint = mode === 'login' ? '/auth/login' : '/auth/register';
      const submitData = mode === 'login' ? 
        { email: formData.email, password: formData.password } : 
        formData;
      
      const response = await axios.post(`${API}${endpoint}`, submitData);
      
      toast.success(mode === 'login' ? 'Welcome back!' : 'Account created successfully!');
      onSuccess(response.data.token, response.data.user);
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  if (mode === 'register' && showAccountTypeModal) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-3xl font-heading font-bold text-center mb-2">
              Choose Your Account Type
            </DialogTitle>
            <DialogDescription className="text-center text-slate-600">
              Select the option that best fits your needs
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-6 mt-6 pb-4">
            {/* DIY Client */}
            <button
              data-testid="account-type-diy"
              onClick={() => handleAccountTypeSelect('diy')}
              className="group w-full border-2 border-slate-200 rounded-2xl p-6 hover:border-violet-500 hover:shadow-xl transition-all text-left bg-white"
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-2xl font-heading font-bold mb-1 group-hover:text-violet-600 transition-colors">
                    DIY Client
                  </h3>
                  <p className="text-slate-600">Perfect for personal credit repair</p>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-violet-600">$59.99</div>
                  <div className="text-sm text-slate-500">per month</div>
                </div>
              </div>
              <ul className="space-y-2 text-sm text-slate-700">
                <li className="flex items-center gap-2">
                  <span className="text-green-500">✓</span> Unlimited AI-generated dispute letters
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-500">✓</span> 3-bureau dispute tracking
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-500">✓</span> Personal credit dashboard
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-500">✓</span> Email support
                </li>
              </ul>
            </button>

            {/* Broker Pro */}
            <button
              data-testid="account-type-broker-pro"
              onClick={() => handleAccountTypeSelect('broker_pro')}
              className="group w-full border-2 border-slate-200 rounded-2xl p-6 hover:border-violet-500 hover:shadow-xl transition-all text-left bg-white"
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-2xl font-heading font-bold mb-1 group-hover:text-violet-600 transition-colors">
                    Broker Pro
                  </h3>
                  <p className="text-slate-600">For credit repair professionals</p>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-violet-600">$199</div>
                  <div className="text-sm text-slate-500">per month</div>
                </div>
              </div>
              <ul className="space-y-2 text-sm text-slate-700">
                <li className="flex items-center gap-2">
                  <span className="text-green-500">✓</span> Everything in DIY, plus...
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-500">✓</span> Client management CRM
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-500">✓</span> Manage up to 50 clients
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-500">✓</span> Bulk dispute generation
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-500">✓</span> Priority support
                </li>
              </ul>
            </button>

            {/* Broker Enterprise */}
            <button
              data-testid="account-type-broker-enterprise"
              onClick={() => handleAccountTypeSelect('broker_enterprise')}
              className="group w-full border-2 border-violet-500 rounded-2xl p-6 hover:shadow-2xl transition-all text-left bg-gradient-to-br from-violet-50 to-purple-50 relative"
            >
              <div className="absolute top-4 right-4 bg-violet-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                MOST POPULAR
              </div>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-2xl font-heading font-bold mb-1 text-violet-600">
                    Broker Enterprise
                  </h3>
                  <p className="text-slate-700 font-medium">Scale your credit repair business</p>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-violet-600">$299</div>
                  <div className="text-sm text-slate-600">per month</div>
                </div>
              </div>
              <ul className="space-y-2 text-sm text-slate-700">
                <li className="flex items-center gap-2">
                  <span className="text-violet-600">✓</span> Everything in Pro, plus...
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-violet-600">✓</span> Unlimited clients
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-violet-600">✓</span> White-label options
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-violet-600">✓</span> Custom branding
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-violet-600">✓</span> Dedicated account manager
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-violet-600">✓</span> API access
                </li>
              </ul>
            </button>
            
            {/* Identity IQ Members - Quick Import */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t-2 border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500 font-medium">Already have Identity IQ?</span>
              </div>
            </div>
            
            <button
              onClick={() => handleAccountTypeSelect('identityiq_import')}
              className="group w-full border-2 border-blue-300 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6 hover:border-blue-500 hover:shadow-xl transition-all text-left"
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-2xl">🔐</span>
                    <h3 className="text-2xl font-heading font-bold group-hover:text-blue-600 transition-colors">
                      Identity IQ Members
                    </h3>
                  </div>
                  <p className="text-slate-700 font-medium">Quick setup with your existing credit report</p>
                </div>
              </div>
              <ul className="space-y-2 text-sm text-slate-700">
                <li className="flex items-center gap-2">
                  <span className="text-blue-600">✓</span> Import your credit report in seconds
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-blue-600">✓</span> Auto-detect negative accounts
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-blue-600">✓</span> Start disputing immediately
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-blue-600">✓</span> DIY Plan included ($59.99/mo)
                </li>
              </ul>
              <div className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-full text-center font-semibold">
                Quick Start →
              </div>
            </button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Identity IQ Signup Modal (No JSON required yet)
  if (showIdentityIQImport) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-3xl font-heading font-bold text-center mb-2">
              <span className="text-2xl mr-2">🔐</span>
              Identity IQ Members - Quick Setup
            </DialogTitle>
            <DialogDescription className="text-center text-slate-600">
              Create your account now, then import your report in the next step
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            {/* Why Identity IQ Members */}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
              <h3 className="font-semibold text-blue-900 mb-2">✨ Perfect for Identity IQ Customers</h3>
              <ul className="space-y-2 text-sm text-blue-800">
                <li className="flex items-center gap-2">
                  <span>✓</span> Already have your credit report from Identity IQ?
                </li>
                <li className="flex items-center gap-2">
                  <span>✓</span> We'll help you import it in the next step
                </li>
                <li className="flex items-center gap-2">
                  <span>✓</span> Start disputing negative items immediately
                </li>
              </ul>
            </div>

            {/* Registration Form */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Create Your DisputeKing.AI Account</h3>
              <div>
                <Label>Full Name</Label>
                <Input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  placeholder="John Doe"
                  required
                  className="rounded-xl"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="you@example.com"
                  required
                  className="rounded-xl"
                />
              </div>
              <div>
                <Label>Password</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Create a secure password"
                  required
                  className="rounded-xl"
                />
              </div>
            </div>

            {/* What Happens Next */}
            <div className="bg-slate-50 rounded-xl p-4">
              <h3 className="font-semibold mb-2">📋 What Happens Next:</h3>
              <ol className="list-decimal list-inside space-y-1 text-sm text-slate-700">
                <li>Click "Create Account" below</li>
                <li>You'll be taken to your dashboard</li>
                <li>Click "Import Identity IQ JSON" button</li>
                <li>Follow the simple instructions to get your JSON</li>
                <li>Paste it and start disputing!</li>
              </ol>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                onClick={handleIdentityIQSignup}
                disabled={loading || !formData.email || !formData.password || !formData.full_name}
                className="flex-1 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-full font-heading font-semibold text-lg py-6"
              >
                {loading ? 'Creating Account...' : '✅ Create Account ($59.99/mo)'}
              </Button>
              <Button
                onClick={() => {
                  setShowIdentityIQImport(false);
                  setShowAccountTypeModal(true);
                }}
                variant="outline"
                className="rounded-full font-heading"
                disabled={loading}
              >
                Back
              </Button>
            </div>

            <p className="text-xs text-center text-slate-500">
              7-day free trial • Cancel anytime • By creating an account, you agree to our Terms of Service
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-heading font-bold">
            {mode === 'login' ? 'Welcome Back' : 
              formData.account_type === 'broker' ? 
                `Create ${formData.broker_tier === 'enterprise' ? 'Enterprise' : 'Pro'} Account` : 
                'Create DIY Account'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {mode === 'register' && (
            <>
              <div>
                <Label htmlFor="full_name">Full Name {formData.account_type === 'broker' && '/ Contact Person'}</Label>
                <Input
                  id="full_name"
                  data-testid="full-name-input"
                  type="text"
                  required
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="rounded-xl"
                />
              </div>
              {formData.account_type === 'broker' && (
                <div>
                  <Label htmlFor="broker_company">Company Name</Label>
                  <Input
                    id="broker_company"
                    data-testid="broker-company-input"
                    type="text"
                    required
                    value={formData.broker_company}
                    onChange={(e) => setFormData({ ...formData, broker_company: e.target.value })}
                    className="rounded-xl"
                    placeholder="Your Credit Repair Business"
                  />
                </div>
              )}
            </>
          )}
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              data-testid="email-input"
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="rounded-xl"
            />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              data-testid="password-input"
              type="password"
              required
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="rounded-xl"
            />
          </div>
          {mode === 'register' && (
            <div className="bg-slate-50 rounded-xl p-4 text-sm">
              <div className="font-semibold mb-1">
                {formData.account_type === 'broker' ? 
                  `Broker ${formData.broker_tier === 'enterprise' ? 'Enterprise' : 'Pro'}` : 
                  'DIY Plan'} - ${
                    formData.account_type === 'broker' ? 
                      (formData.broker_tier === 'enterprise' ? '299.00' : '199.00') : 
                      '59.99'
                  }/month
              </div>
              <div className="text-slate-600">7-day free trial included</div>
            </div>
          )}
          <Button
            data-testid="auth-submit-button"
            type="submit"
            disabled={loading}
            className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-full py-6 font-heading font-semibold"
          >
            {loading ? 'Processing...' : mode === 'login' ? 'Login' : 'Create Account'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;